/**
 * hotels-service wire shapes, derived from the generated OpenAPI types, plus
 * the mappers that translate them into the frontend types.
 *
 * Every type here is an alias into `@mangovoice/cx-hotels-service-client`'s
 * generated `schema.ts` rather than a hand-written mirror. That is deliberate:
 * `scripts/sync-cx-specs.sh` regenerates that schema from the committed spec,
 * so a field rename or removal upstream becomes a typecheck failure here
 * instead of a runtime 4xx/undefined. Do not re-declare these shapes locally.
 *
 * hotels-service is UUID-keyed and uses different field names than the legacy
 * mango-api hospitality proxy, so these adapters keep the naming differences
 * out of the components.
 */
import type {
  components,
  operations,
} from '@mangovoice/cx-hotels-service-client'

import type { WakeupCall } from '../types'

type Schemas = components['schemas']

/**
 * Max wake-up calls hotels-service accepts in one bulk create request.
 * Mirrors the spec's `maxItems` on the array form of the create body — see the
 * compile-time assertion below.
 */
export const BULK_CREATE_WAKEUP_CALLS_MAX = 25

/** `Pbx` — `legacy_id` bridges to the numeric id mango-api uses. */
export type GatewayPbx = Schemas['Pbx']

/** `WakeUpCall` — keyed by `uuid`, with `legacy_id` for the mango-api row. */
export type GatewayWakeupCall = Schemas['WakeUpCall']

/** `Room` — keyed by `extension_uuid`. */
export type GatewayRoom = Schemas['Room']

/** Paginated envelopes returned by the list endpoints. */
export type GatewayRoomList = Schemas['PaginatedRoomList']
export type GatewayWakeupCallList = Schemas['PaginatedWakeUpCallList']

/** `BulkCreateWakeUpCallsResponse` */
export type GatewayBulkCreateResponse = Schemas['BulkCreateWakeUpCallsResponse']

/** `BulkUpdateResponse` — returned by the bulk room PATCH. */
export type GatewayBulkUpdateResponse = Schemas['BulkUpdateResponse']

/**
 * The wake-up call bulk DELETE response. Declared inline in the spec rather
 * than as a named schema, so it is read off the operation.
 */
export type GatewayDeleteResponse =
  operations['hotelsV1DeletePbxWakeUpCalls']['responses'][200]['content']['application/json']

/** Request bodies. */
export type GatewayUpdatePbxRequest = Schemas['UpdatePbxRequest']
export type GatewayUpdateRoomRequest = Schemas['UpdateRoomRequest']
export type GatewayCreateWakeupCallRequest = Schemas['CreateWakeUpCallRequest']

/**
 * Fails to compile if the spec's `maxItems` on the bulk-create array body
 * stops matching {@link BULK_CREATE_WAKEUP_CALLS_MAX}. openapi-typescript
 * doesn't emit `maxItems`, so this asserts the body is still the
 * single-or-array union the chunking logic assumes.
 */
type BulkCreateBody =
  operations['hotelsV1CreatePbxWakeUpCalls']['requestBody']['content']['application/json']
type _AssertBulkCreateAcceptsArray =
  GatewayCreateWakeupCallRequest[] extends BulkCreateBody ? true : never
const _assertBulkCreateAcceptsArray: _AssertBulkCreateAcceptsArray = true
void _assertBulkCreateAcceptsArray

/**
 * hotels-service identifies wake-up calls by `uuid`; the frontend's
 * `WakeupCall.id` is whatever identifier the delete endpoints accept, which is
 * the UUID.
 *
 * Every field on the generated `WakeUpCall` is optional (the spec marks none
 * `required`), so the mapper fills the defaults the UI relies on rather than
 * spreading a partially-populated object.
 */
export const normalizeWakeupCall = (call: GatewayWakeupCall): WakeupCall => ({
  ...call,
  id: call.uuid ?? '',
  pbx_uuid: call.pbx_uuid ?? '',
  extension_uuid: call.extension_uuid ?? '',
  wuc_timestamp: call.wuc_timestamp ?? '',
  done: call.done ?? false,
  status: call.status ?? 0,
  answered_at: call.answered_at ?? null,
  attempted_at: call.attempted_at ?? null,
  created_at: call.created_at ?? '',
  deleted_at: call.deleted_at ?? null,
  origination: call.origination ?? 0,
  attempt_count: call.attempt_count ?? 0,
})

/** Split `items` into batches of at most `size`. */
export const chunk = <T>(items: T[], size: number): T[][] => {
  if (items.length <= size) return [items]
  const batches: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size))
  }
  return batches
}
